/**
 * PoisTableRow - Linha da tabela de POIs
 * Memoizado para evitar re-renders desnecessários
 */

import React, { memo } from 'react';
import {
  TableRow,
  TableCell,
  Box,
  Typography,
  Checkbox,
  Chip,
  Tooltip,
  IconButton,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

import { usePremiumTokens } from '../../common/theme/usePremiumTokens';
import { useTranslation } from '../../common/components/LocalizationProvider';
import { PoiMarkerMini } from '../PoiMarkerPreview';
import CollectionActions from './CollectionActions';

const PoisTableRow = memo(({
  row,
  groups,
  icons,
  onRefresh,
  onToggleVisibility,
  onToggleFavorite,
  selected = false,
  onSelect,
}) => {
  const t = useTranslation();
  const tokens = usePremiumTokens();

  // Get icon info for POI
  const getPoiIconInfo = () => {
    if (row.iconType === 'lucide' && row.iconCode) {
      return { iconType: 'lucide', iconCode: row.iconCode };
    }
    if (row.iconId) {
      const icon = icons?.find((i) => i.id === row.iconId);
      if (icon) {
        return { iconType: 'png', path: `/images/${icon.path}` };
      }
    }
    return null;
  };

  const iconInfo = getPoiIconInfo();
  const group = groups?.[row.groupId];

  // Cor unificada: prioridade grupo > poi > tema
  const poiColor = group?.color || row.color || tokens?.accent?.primary;

  return (
    <TableRow
      hover
      selected={selected}
      sx={{
        cursor: 'default',
        transition: tokens?.transitions?.colors || 'all 0.15s ease',
        '&:hover': {
          backgroundColor: tokens?.surface?.hover || 'rgba(255,255,255,0.03)',
        },
        '&.Mui-selected': {
          backgroundColor: `${tokens?.accent?.primary}10`,
          '&:hover': {
            backgroundColor: `${tokens?.accent?.primary}15`,
          },
        },
      }}
    >
      {/* Checkbox */}
      {onSelect && (
        <TableCell padding="checkbox" sx={{ py: 1, width: 48 }}>
          <Checkbox
            checked={selected}
            onChange={() => onSelect(row.id)}
            sx={{
              color: tokens?.text?.tertiary,
              '&.Mui-checked': {
                color: tokens?.accent?.primary,
              },
            }}
          />
        </TableCell>
      )}

      {/* Marcador com cor unificada */}
      <TableCell sx={{ py: 1, width: 50 }}>
        <PoiMarkerMini
          icon={iconInfo}
          color={poiColor}
          size={32}
        />
      </TableCell>

      {/* Favorito */}
      <TableCell sx={{ py: 1, width: 40 }}>
        <Tooltip title={row.favorite ? t('poiRemoveFavorite') || 'Remover favorito' : t('poiAddFavorite') || 'Adicionar favorito'}>
          <IconButton size="small" onClick={() => onToggleFavorite(row)}>
            {row.favorite ? (
              <StarIcon sx={{ color: tokens?.semantic?.warning }} fontSize="small" />
            ) : (
              <StarBorderIcon sx={{ color: tokens?.text?.muted }} fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
      </TableCell>

      {/* Nome + Descrição */}
      <TableCell sx={{ py: 1 }}>
        <Box>
          <Typography variant="body2" fontWeight={500}>
            {row.name}
          </Typography>
          {row.description && (
            <Typography
              variant="caption"
              sx={{ color: tokens?.text?.muted, display: 'block' }}
              noWrap
            >
              {row.description}
            </Typography>
          )}
        </Box>
      </TableCell>

      {/* Cidade */}
      <TableCell sx={{ py: 1 }}>
        {row.city ? (
          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
            {row.city}
            {row.state && (
              <Typography component="span" variant="caption" sx={{ color: tokens?.text?.muted }}>
                {` - ${row.state}`}
              </Typography>
            )}
          </Typography>
        ) : (
          <Typography variant="body2" sx={{ color: tokens?.text?.muted }}>-</Typography>
        )}
      </TableCell>

      {/* Grupo - usa mesma cor do marcador (poiColor) */}
      <TableCell sx={{ py: 1 }}>
        {group ? (
          <Chip
            label={group.name}
            size="small"
            className="poi-group-chip"
            sx={{
              height: 24,
              fontSize: '0.75rem',
              fontWeight: 600,
              backgroundColor: `${poiColor}25 !important`,
              color: `${poiColor} !important`,
              border: `2px solid ${poiColor} !important`,
              '& .MuiChip-label': { px: 1.5 },
            }}
          />
        ) : (
          <Typography variant="body2" sx={{ color: tokens?.text?.muted }}>-</Typography>
        )}
      </TableCell>

      {/* Visibilidade */}
      <TableCell align="center" sx={{ py: 1 }}>
        <Tooltip title={row.visible ? t('sharedHide') : t('sharedShow')}>
          <IconButton size="small" onClick={() => onToggleVisibility(row)}>
            {row.visible ? (
              <VisibilityIcon sx={{ color: tokens?.semantic?.success }} fontSize="small" />
            ) : (
              <VisibilityOffIcon sx={{ color: tokens?.semantic?.warning }} fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
      </TableCell>

      {/* Actions */}
      <TableCell align="right" sx={{ py: 1 }}>
        <CollectionActions
          itemId={row.id}
          editPath="/settings/poi"
          endpoint="pois"
          setTimestamp={onRefresh}
        />
      </TableCell>
    </TableRow>
  );
});

PoisTableRow.displayName = 'PoisTableRow';

export default PoisTableRow;
